import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Five Below brand defaults — only used as a last-resort fallback before
// /api/settings has responded. The pre-React bootstrap script in index.html
// already pulls the cached `ecDashAppName` from localStorage, so on every
// load after the first there's no name flicker.
const DEFAULT_THEME = {
  logo_url: "/fivebelow-logo.png",
  favicon_url: "/fivebelow-logo.png",
  primary_color: "#0066FF",
  accent_color: "#FACC15",
  background_color: "#F5F5F5",
  text_color: "#1A1A1A",
  // Intentionally generic so first-ever load doesn't flash a stale store
  // name. Once /api/settings returns, the real app_name overrides this.
  app_name: "Wow App",
  tagline: "Capture every email. Hit every goal.",
};

const ThemeContext = createContext({
  theme: DEFAULT_THEME,
  settings: null,
  refresh: () => {},
  applyPreview: () => {},
  clearPreview: () => {},
});

// Lightens/darkens a hex color by `amt` (e.g. -0.1 to darken 10%).
const shade = (hex, amt) => {
  const h = (hex || "").replace("#", "");
  if (!h) return hex;
  const n = parseInt(h.length === 3 ? h.split("").map((c) => c + c).join("") : h, 16);
  let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  r = Math.max(0, Math.min(255, Math.round(r + amt * 255)));
  g = Math.max(0, Math.min(255, Math.round(g + amt * 255)));
  b = Math.max(0, Math.min(255, Math.round(b + amt * 255)));
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("")}`;
};

// Convert "#0066FF" → "0 102 255" (space-separated) so it works inside the
// modern `rgb(R G B / alpha)` syntax used by our theme overrides.
const hexToRgb = (hex) => {
  const h = (hex || "").replace("#", "");
  if (!h) return "0 102 255";
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

const applyCssVars = (theme) => {
  const root = document.documentElement;
  root.style.setProperty("--fb-blue", theme.primary_color);
  root.style.setProperty("--fb-blue-dark", shade(theme.primary_color, -0.1));
  root.style.setProperty("--primary", theme.primary_color);
  root.style.setProperty("--primary-hover", shade(theme.primary_color, -0.1));
  root.style.setProperty("--accent", theme.accent_color);
  root.style.setProperty("--fb-accent", theme.accent_color);
  root.style.setProperty("--background", theme.background_color);
  // 2026-08-29 — the body element reads `--body-bg` (see index.css),
  // NOT `--background`, so the two vars must stay in sync or dark
  // theme presets (Halloween #0F0F14, Midnight #0B1220, etc.) leak a
  // cream default body behind their brand background and every pale
  // theme-text-color label vanishes. Fix: mirror `--background` into
  // `--body-bg` here so `body { background-color: var(--body-bg) }`
  // actually flips with the preset.
  root.style.setProperty("--body-bg", theme.background_color);
  // Safety guard — the app is card-based with lots of white surfaces. If
  // a theme saves a near-white `text_color` (bug 2026-07-31: an older
  // Spider-Man preset persisted `#F8FAFC`), inheriting it via
  // `body { color: var(--text-primary) }` turned every un-overridden
  // label invisible on white cards. Any theme text with luminance above
  // ~0.85 falls back to the safe slate-900 default, and the theme's
  // literal value is still available via `--on-brand-text` for hero
  // surfaces that intentionally sit on the dark brand background.
  const tc = theme.text_color || "#1A1A1A";
  const [tr, tg, tb] = hexToRgb(tc).split(" ").map(Number);
  const tcLum = (0.2126 * tr + 0.7152 * tg + 0.0722 * tb) / 255;
  const safeText = tcLum > 0.85 ? "#1A1A1A" : tc;
  root.style.setProperty("--text-primary", safeText);
  root.style.setProperty("--fb-black", safeText);
  root.style.setProperty("--on-brand-text", tc);
  // RGB triplets so utility classes can do `rgb(var(--primary-rgb) / 0.1)`
  // for soft tints (50/100 backgrounds, hover rings, etc.).
  root.style.setProperty("--primary-rgb", hexToRgb(theme.primary_color));
  root.style.setProperty("--accent-rgb", hexToRgb(theme.accent_color));
  // HomePage uses an opaque gradient that ends in solid off-white so
  // the tile stack has a floor. On themes with a dark brand background
  // (Blueprint, Spider-Man), that floor hides the SpiderManTheme city
  // skyline that lives behind it. Expose the "gradient end" as a var
  // so HomePage can flip to a transparent floor when the theme bg is
  // dark — letting the city + theme background show through.
  const [bgR, bgG, bgB] = hexToRgb(theme.background_color || "#F5F5F5").split(" ").map(Number);
  const bgLum = (0.2126 * bgR + 0.7152 * bgG + 0.0722 * bgB) / 255;
  root.style.setProperty("--home-bg-end", bgLum < 0.3 ? "rgba(0,0,0,0)" : "rgba(245,245,244,1)");

  // Pitch Black event skin — toggle the scoped root class that powers
  // the liquid-glass overrides in styles/pitch-black.css. Matched by the
  // preset's unique neon signature so it never leaks into other themes.
  try {
    const pb = (theme.primary_color || "").toLowerCase() === "#ec008c"
      && (theme.accent_color || "").toLowerCase() === "#a3e635";
    document.documentElement.classList.toggle("theme-pitch-black", pb);
  } catch { /* non-browser env */ }
};

// The app was originally built around hardcoded Tailwind blue/yellow classes
// (`bg-blue-600`, `text-blue-700`, `from-blue-50`, `ring-blue-400`, etc.) and
// inline `#0066FF` styles. That meant changing the theme via Settings updated
// the CSS variables but didn't actually re-skin the UI — the hardcoded
// classes kept rendering Five Below blue regardless of preset. (User report
// 2026-05-03 "seasonal themes that dont change the app at all".)
//
// The fix below injects a dynamic <style id="fb-theme-overrides"> block that
// remaps every common Tailwind blue + yellow shade to the active theme's
// primary/accent (with a sensible alpha for the soft 50/100/200 shades). The
// override is rebuilt on every theme change and removed if the theme matches
// the original Five Below palette so the default look stays pixel-identical.
const FB_DEFAULT_PRIMARY = "#0066FF";
const FB_DEFAULT_ACCENT = "#FACC15";

const buildThemeOverrideCss = (theme) => {
  const primary = theme.primary_color || FB_DEFAULT_PRIMARY;
  const accent = theme.accent_color || FB_DEFAULT_ACCENT;
  // Skip the override entirely when the theme equals the FB defaults — keeps
  // the original Tailwind palette pixel-perfect and trims paint work.
  if (primary.toLowerCase() === FB_DEFAULT_PRIMARY.toLowerCase()
      && accent.toLowerCase() === FB_DEFAULT_ACCENT.toLowerCase()) {
    return "";
  }
  const pRgb = hexToRgb(primary);
  const aRgb = hexToRgb(accent);
  const pDark = shade(primary, -0.1);
  const pDarker = shade(primary, -0.2);
  const aDark = shade(accent, -0.1);

  // !important is intentional — Tailwind ships these as utility classes that
  // would otherwise win against any vanilla CSS we inject. Scoped to color/bg
  // properties only so layout (sizing, spacing) is untouched.
  return `
/* Auto-generated theme overrides — see ThemeContext.js#buildThemeOverrideCss */
.bg-blue-50  { background-color: rgb(${pRgb} / 0.08) !important; }
.bg-blue-100 { background-color: rgb(${pRgb} / 0.16) !important; }
.bg-blue-200 { background-color: rgb(${pRgb} / 0.28) !important; }
.bg-blue-500 { background-color: ${pDark} !important; }
.bg-blue-600 { background-color: ${primary} !important; }
.bg-blue-700 { background-color: ${pDark} !important; }
.bg-blue-900 { background-color: ${pDarker} !important; }

.text-blue-500 { color: ${pDark} !important; }
.text-blue-600 { color: ${primary} !important; }
.text-blue-700 { color: ${pDark} !important; }
.text-blue-800 { color: ${pDarker} !important; }
.text-blue-900 { color: ${pDarker} !important; }

.border-blue-100 { border-color: rgb(${pRgb} / 0.20) !important; }
.border-blue-200 { border-color: rgb(${pRgb} / 0.32) !important; }
.border-blue-300 { border-color: rgb(${pRgb} / 0.45) !important; }
.border-blue-400 { border-color: ${primary} !important; }
.border-blue-500 { border-color: ${pDark} !important; }
.border-blue-600 { border-color: ${primary} !important; }

.ring-blue-200 { --tw-ring-color: rgb(${pRgb} / 0.32) !important; }
.ring-blue-300 { --tw-ring-color: rgb(${pRgb} / 0.45) !important; }
.ring-blue-400 { --tw-ring-color: ${primary} !important; }
.ring-blue-500 { --tw-ring-color: ${pDark} !important; }

.from-blue-50  { --tw-gradient-from: rgb(${pRgb} / 0.08) var(--tw-gradient-from-position) !important; --tw-gradient-to: rgb(${pRgb} / 0) var(--tw-gradient-to-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
.to-blue-50    { --tw-gradient-to: rgb(${pRgb} / 0.08) var(--tw-gradient-to-position) !important; }
.from-blue-100 { --tw-gradient-from: rgb(${pRgb} / 0.16) var(--tw-gradient-from-position) !important; --tw-gradient-to: rgb(${pRgb} / 0) var(--tw-gradient-to-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
.from-blue-500 { --tw-gradient-from: ${pDark} var(--tw-gradient-from-position) !important; --tw-gradient-to: rgb(${pRgb} / 0) var(--tw-gradient-to-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
.from-blue-600 { --tw-gradient-from: ${primary} var(--tw-gradient-from-position) !important; --tw-gradient-to: rgb(${pRgb} / 0) var(--tw-gradient-to-position) !important; --tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to) !important; }
.to-blue-600   { --tw-gradient-to: ${primary} var(--tw-gradient-to-position) !important; }
.to-blue-700   { --tw-gradient-to: ${pDark} var(--tw-gradient-to-position) !important; }

/* Accent (yellow) family — maps to the chosen accent color. Uses softer alpha
   tints for 50/100 since accent colors tend to be saturated. */
.bg-yellow-50  { background-color: rgb(${aRgb} / 0.10) !important; }
.bg-yellow-100 { background-color: rgb(${aRgb} / 0.20) !important; }
.bg-yellow-300 { background-color: ${accent} !important; }
.bg-yellow-400 { background-color: ${accent} !important; }
.bg-yellow-500 { background-color: ${aDark} !important; }
.bg-amber-300  { background-color: ${accent} !important; }
.bg-amber-400  { background-color: ${accent} !important; }
.bg-amber-500  { background-color: ${aDark} !important; }

.text-yellow-500 { color: ${aDark} !important; }
.text-yellow-600 { color: ${aDark} !important; }
.text-amber-500  { color: ${accent} !important; }
.text-amber-600  { color: ${aDark} !important; }
.text-amber-700  { color: ${aDark} !important; }

.border-yellow-200 { border-color: rgb(${aRgb} / 0.4) !important; }
.border-amber-200  { border-color: rgb(${aRgb} / 0.4) !important; }
.border-amber-300  { border-color: rgb(${aRgb} / 0.55) !important; }

.ring-amber-300 { --tw-ring-color: rgb(${aRgb} / 0.55) !important; }
.ring-amber-400 { --tw-ring-color: ${accent} !important; }
`;
};

const applyThemeOverrides = (theme) => {
  const id = "fb-theme-overrides";
  const css = buildThemeOverrideCss(theme);
  let style = document.getElementById(id);
  if (!css) {
    if (style) style.remove();
    return;
  }
  if (!style) {
    style = document.createElement("style");
    style.id = id;
    document.head.appendChild(style);
  }
  if (style.textContent !== css) style.textContent = css;
};

// Rewrites favicon + apple-touch-icon + dynamic webmanifest so the browser tab,
// iOS "Add to Home Screen", and Android "Install App" all pick up the admin's logo.
const applyIcons = (favicon, appleIcon, theme) => {
  // Favicon (browser tab). Removing + re-adding forces Chrome/Edge/Safari to refresh.
  document.querySelectorAll("link[rel='icon'], link[rel='shortcut icon']").forEach((n) => n.remove());
  if (favicon) {
    const link = document.createElement("link");
    link.rel = "icon";
    link.type = favicon.startsWith("data:") ? favicon.slice(5, favicon.indexOf(";")) : "image/png";
    link.href = favicon;
    document.head.appendChild(link);
  }
  // iOS "Add to Home Screen" picks up apple-touch-icon (prefers higher-res; use logo).
  document.querySelectorAll("link[rel='apple-touch-icon']").forEach((n) => n.remove());
  const apple = appleIcon || favicon;
  if (apple) {
    [180, 167, 152, 120].forEach((size) => {
      const l = document.createElement("link");
      l.rel = "apple-touch-icon";
      l.setAttribute("sizes", `${size}x${size}`);
      l.href = apple;
      document.head.appendChild(l);
    });
  }
  // Android Chrome "Install" / "Add to Home Screen" reads the webmanifest for icons.
  // Build a dynamic manifest as a blob URL so admin uploads work without touching files on disk.
  try {
    const manifest = {
      name: theme.app_name || "Wow App",
      short_name: (theme.app_name || "Wow App").slice(0, 12),
      description: theme.tagline || "Five Below email capture tracker",
      start_url: "/",
      display: "standalone",
      background_color: theme.background_color || "#F5F5F5",
      theme_color: theme.primary_color || "#0066FF",
      icons: apple ? [
        { src: apple, sizes: "192x192", type: "image/png", purpose: "any maskable" },
        { src: apple, sizes: "512x512", type: "image/png", purpose: "any maskable" },
      ] : [],
    };
    const blob = new Blob([JSON.stringify(manifest)], { type: "application/manifest+json" });
    const url = URL.createObjectURL(blob);
    document.querySelectorAll("link[rel='manifest']").forEach((n) => n.remove());
    const link = document.createElement("link");
    link.rel = "manifest";
    link.href = url;
    document.head.appendChild(link);
  } catch (e) { /* ignore in non-browser envs */ }

  // theme-color meta drives the iOS/Android address-bar color.
  let meta = document.querySelector("meta[name='theme-color']");
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = theme.primary_color || "#0066FF";
};

const applyTitle = (name) => {
  if (!name) return;
  // 2026-05-25: branding consolidated to "WoW App" (per user). Tab title +
  // iOS home-screen label always read "WoW App" so the saved-to-phone
  // experience is consistent — per-store names live in the page H1
  // instead. Skip the document.title write so the index.html title
  // sticks; we still cache the per-store name so the bootstrap H1 can
  // pick it up without flicker.
  document.title = "WoW App";
  try { localStorage.setItem("ecDashAppName", name); } catch { /* ignore */ }
};

export function ThemeProvider({ children }) {
  // Cache the LAST-applied settings in localStorage so the very first
  // paint on any subsequent load matches the theme the user actually
  // saw last time — not the built-in Five Below default. Fixes the
  // "home screen flashes blue then switches back to the current theme"
  // report (2026-02-27). If parsing fails or the cache is missing (e.g.
  // first-ever load) we fall through to `null`, which renders the
  // DEFAULT_THEME as before.
  const SETTINGS_CACHE_KEY = "ecDashSettingsCache";
  const initialCached = (() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(SETTINGS_CACHE_KEY) : null;
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch { return null; }
  })();
  const [settings, setSettings] = useState(initialCached);
  const [previewTheme, setPreviewTheme] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data);
      // Persist the fresh payload so tomorrow's first paint uses the
      // real theme instead of the DEFAULT_THEME fallback.
      try {
        localStorage.setItem(SETTINGS_CACHE_KEY, JSON.stringify(res.data || {}));
      } catch { /* quota / disabled */ }
    } catch (e) {
      console.error("Failed to fetch settings:", e);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  // Strip out non-hex / empty / null color values from the API payload so a
  // single bad write to MongoDB can't permanently break the theme. Anything
  // that isn't a parseable hex falls back to DEFAULT_THEME via the spread.
  // (Bug 2026-05-03: a previous build accidentally persisted `var(--fb-blue)`
  // strings to settings, which made `var(--fb-accent)` resolve empty and
  // wiped out the May Contest icon gradient.)
  const sanitizeSettings = (s) => {
    if (!s) return s;
    const out = { ...s };
    const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
    ["primary_color", "accent_color", "background_color", "text_color"].forEach((k) => {
      const v = out[k];
      if (typeof v !== "string" || !HEX.test(v.trim())) delete out[k];
    });
    return out;
  };

  const effective = useMemo(() => {
    const base = { ...DEFAULT_THEME, ...sanitizeSettings(settings || {}) };
    return previewTheme ? { ...base, ...previewTheme } : base;
  }, [settings, previewTheme]);

  useEffect(() => {
    applyCssVars(effective);
    applyThemeOverrides(effective);
    // apple-touch-icon uses the higher-res logo for Home-Screen installs; favicon for the tab.
    applyIcons(effective.favicon_url, effective.logo_url, effective);
    // Only persist the title once the real settings payload has resolved —
    // we don't want to cache the placeholder DEFAULT_THEME.app_name on first
    // mount and then stomp the user's actual name on next load.
    if (settings) applyTitle(effective.app_name);
  }, [effective, settings]);

  const value = useMemo(() => ({
    theme: effective,
    settings,
    refresh: fetchSettings,
    applyPreview: setPreviewTheme,
    clearPreview: () => setPreviewTheme(null),
  }), [effective, settings, fetchSettings]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
